<template>
  <q-page class="learn-course-page">
    <div class="learn-container">
      <!-- Loading state -->
      <template v-if="loading">
        <div class="header-skeleton">
          <q-skeleton type="circle" size="96px" dark />
          <div style="flex: 1;">
            <q-skeleton type="text" width="120px" height="14px" dark />
            <q-skeleton type="text" width="280px" height="28px" dark style="margin-top: 4px;" />
            <q-skeleton type="text" width="160px" height="14px" dark style="margin-top: 8px;" />
          </div>
        </div>
        <div class="units-skeleton">
          <q-skeleton v-for="i in 3" :key="i" type="rect" height="200px" dark class="unit-skeleton" />
        </div>
      </template>

      <!-- Error state -->
      <div v-else-if="error" class="error-card">
        <q-icon name="error_outline" size="48px" :style="{ color: 'var(--oa-danger)' }" />
        <p class="error-text">No se pudo cargar el curso</p>
        <p class="error-detail">{{ error }}</p>
        <q-btn
          no-caps
          unelevated
          label="Reintentar"
          :style="{ background: 'var(--oa-gold-full)', color: 'var(--color-white-cap)', fontWeight: '600', borderRadius: '8px' }"
          @click="fetchData"
        />
      </div>

      <!-- Content -->
      <template v-else-if="course">
        <!-- Course header card -->
        <div class="course-header-card">
          <div class="header-glow" />
          <div class="header-glow-bottom" />
          <div class="header-content">
            <!-- Progress circle -->
            <div class="progress-circle-wrapper">
              <svg class="progress-svg" viewBox="0 0 96 96">
                <circle cx="48" cy="48" r="42" fill="none" stroke="rgba(255,255,255,0.08)" stroke-width="6" />
                <circle
                  cx="48" cy="48" r="42" fill="none"
                  stroke="rgb(46, 127, 181)"
                  stroke-width="6"
                  stroke-linecap="round"
                  :stroke-dasharray="circumference"
                  :stroke-dashoffset="circumference * (1 - progressPct / 100)"
                  class="progress-ring"
                />
              </svg>
              <div class="progress-circle-text">
                <span class="progress-pct">{{ progressPct }}%</span>
              </div>
            </div>

            <div class="header-info">
              <router-link to="/courses" class="breadcrumb-back">
                &larr; Mis cursos
              </router-link>
              <h1 class="course-title">{{ course.title }}</h1>
              <p class="course-stats">
                {{ completedCount }} de {{ totalLessons }} lecciones completadas
              </p>
            </div>

            <router-link
              v-if="continueLessonId"
              :to="continueLink"
              class="continue-btn"
            >
              Continuar
              <span style="font-size: 18px;">&rarr;</span>
            </router-link>
          </div>
        </div>

        <!-- Units -->
        <div class="units-list">
          <div
            v-for="(unit, i) in courseUnits"
            :key="unit.id"
            class="unit-card"
            :class="{
              'unit-completed': unitMeta[unit.id]?.complete,
              'unit-locked': unitMeta[unit.id]?.locked,
            }"
          >
            <!-- Unit header -->
            <div class="unit-header">
              <div
                class="unit-number"
                :class="{
                  'number-completed': unitMeta[unit.id]?.complete,
                  'number-locked': unitMeta[unit.id]?.locked,
                }"
              >
                <q-icon v-if="unitMeta[unit.id]?.locked" name="lock" size="16px" />
                <q-icon v-else-if="unitMeta[unit.id]?.complete" name="check_circle" size="20px" />
                <span v-else>{{ unit.displayNumber || 'U' + (i + 1) }}</span>
              </div>
              <div class="unit-info">
                <div class="unit-title-row">
                  <span class="unit-display-label">{{ unit.displayNumber || 'U' + (i + 1) }}</span>
                  <h3
                    class="unit-title"
                    :class="{ 'title-locked': unitMeta[unit.id]?.locked }"
                  >
                    {{ unit.title }}
                  </h3>
                  <span v-if="unitMeta[unit.id]?.complete && !unitMeta[unit.id]?.locked" class="badge-completed">Completada</span>
                  <span v-else-if="unitMeta[unit.id]?.started && !unitMeta[unit.id]?.locked" class="badge-progress">En progreso</span>
                </div>
                <div v-if="!unitMeta[unit.id]?.locked" class="unit-progress-row">
                  <div class="unit-progress-track">
                    <div
                      class="unit-progress-fill"
                      :class="{ 'fill-complete': unitMeta[unit.id]?.complete }"
                      :style="{ width: unitMeta[unit.id]?.pct + '%' }"
                    />
                  </div>
                  <span class="unit-progress-text">
                    {{ unitMeta[unit.id]?.completed || 0 }}/{{ unit.lessons?.length || 0 }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Lessons list -->
            <div
              class="unit-lessons"
              :class="{ 'lessons-locked': unitMeta[unit.id]?.locked }"
            >
              <router-link
                v-for="(lesson, j) in (unit.lessons || [])"
                :key="lesson.id"
                :to="lessonHref(lesson)"
                class="lesson-row"
                :class="{
                  'lesson-completed': progressMap[lesson.id]?.completed,
                  'lesson-border': j < (unit.lessons || []).length - 1,
                }"
              >
                <!-- Status circle -->
                <span
                  class="lesson-status"
                  :class="{
                    'status-completed': progressMap[lesson.id]?.completed,
                    'status-default': !progressMap[lesson.id]?.completed,
                  }"
                >
                  {{ progressMap[lesson.id]?.completed ? '\u2713' : (lesson.contentType === 'quiz' ? '?' : j + 1) }}
                </span>

                <!-- Type icon -->
                <span class="lesson-type-icon">
                  {{ lesson.contentType === 'video' ? '\u25B6' : (lesson.contentType === 'quiz' ? '\u2753' : '\u2630') }}
                </span>

                <!-- Title -->
                <span
                  class="lesson-title"
                  :class="{ 'title-completed': progressMap[lesson.id]?.completed }"
                >
                  {{ lesson.title }}
                </span>

                <!-- Arrow -->
                <span class="lesson-arrow">&rarr;</span>
              </router-link>
            </div>
          </div>
        </div>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { api } from 'boot/axios';

interface Lesson {
  id: string;
  title: string;
  contentType: string;
  orderIndex: number;
  estimatedMinutes: number | null;
  xpReward: number;
  isFree: boolean;
}

interface Unit {
  id: string;
  title: string;
  description: string | null;
  orderIndex: number;
  displayNumber: string | null;
  track: string;
  lessons: Lesson[];
}

interface Course {
  id: string;
  title: string;
  slug: string;
  units: Unit[];
}

interface ProgressEntry {
  completed: boolean;
  completedAt: string | null;
  progressPct: number;
}

const route = useRoute();
const courseId = computed(() => route.params.courseId as string);

const loading = ref(true);
const error = ref<string | null>(null);
const course = ref<Course | null>(null);
const progressMap = ref<Record<string, ProgressEntry>>({});
const totalLessons = ref(0);
const completedCount = ref(0);

const circumference = 2 * Math.PI * 42;

const progressPct = computed(() => {
  if (totalLessons.value === 0) return 0;
  return Math.round((completedCount.value / totalLessons.value) * 100);
});

const courseUnits = computed(() => course.value?.units || []);

const unitMeta = computed(() => {
  const meta: Record<string, {
    completed: number;
    total: number;
    pct: number;
    started: boolean;
    complete: boolean;
    locked: boolean;
  }> = {};

  for (const unit of courseUnits.value) {
    const lessons = unit.lessons || [];
    const completedInUnit = lessons.filter((l) => progressMap.value[l.id]?.completed).length;
    const total = lessons.length;
    const pct = total > 0 ? Math.round((completedInUnit / total) * 100) : 0;
    meta[unit.id] = {
      completed: completedInUnit,
      total,
      pct,
      started: completedInUnit > 0,
      complete: completedInUnit === total && total > 0,
      locked: false, // could be based on track/permissions
    };
  }
  return meta;
});

const continueLessonId = computed(() => {
  for (const unit of courseUnits.value) {
    for (const lesson of unit.lessons || []) {
      if (!progressMap.value[lesson.id]?.completed) {
        return lesson.id;
      }
    }
  }
  return null;
});

const continueLink = computed(() => {
  if (!continueLessonId.value) return '';
  // Check if the next lesson is a quiz
  for (const unit of courseUnits.value) {
    const lesson = (unit.lessons || []).find((l) => l.id === continueLessonId.value);
    if (lesson) {
      if (lesson.contentType === 'quiz') return `/quiz/${lesson.id}`;
      return `/learn/${courseId.value}/lesson/${lesson.id}`;
    }
  }
  return `/learn/${courseId.value}/lesson/${continueLessonId.value}`;
});

function lessonHref(lesson: Lesson): string {
  if (lesson.contentType === 'quiz') return `/quiz/${lesson.id}`;
  return `/learn/${courseId.value}/lesson/${lesson.id}`;
}

async function fetchData() {
  loading.value = true;
  error.value = null;

  try {
    // Fetch enrollments to get course slug, then course detail, and progress
    const [enrollmentsRes, progressRes] = await Promise.all([
      api.get('/learn/enrollments'),
      api.get(`/learn/progress/${courseId.value}`),
    ]);

    const enrollments = enrollmentsRes.data;
    const enrollment = enrollments.find(
      (e: { courseId: string }) => e.courseId === courseId.value,
    );

    if (!enrollment) {
      error.value = 'No estás inscripto en este curso';
      loading.value = false;
      return;
    }

    // Get course slug from enrollment
    const slug = enrollment.course?.slug;
    if (!slug) {
      error.value = 'No se pudo determinar el curso';
      loading.value = false;
      return;
    }

    // Fetch full course detail by slug
    const courseRes = await api.get(`/courses/${slug}`);
    course.value = courseRes.data;

    // Process progress
    const progress = progressRes.data;
    progressMap.value = progress.progressMap || {};
    totalLessons.value = progress.total || 0;
    completedCount.value = progress.completed || 0;
  } catch (err) {
    error.value = 'Error al cargar el progreso del curso';
    console.error(err);
  } finally {
    loading.value = false;
  }
}

onMounted(fetchData);
</script>

<style scoped lang="scss">
.learn-course-page {
  padding: 24px;
}

.learn-container {
  max-width: 900px;
  margin: 0 auto;
}

// === Skeleton ===
.header-skeleton {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 32px;
  background: var(--oa-bg-2);
  border-radius: var(--oa-radius-xl);
  border: 1px solid var(--oa-border);
}

.units-skeleton {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 32px;
}

.unit-skeleton {
  border-radius: var(--oa-radius-xl) !important;
}

// === Error ===
.error-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 64px 32px;
  text-align: center;
}

.error-text {
  font-size: var(--oa-text-md);
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.error-detail {
  font-size: var(--oa-text-sm);
  color: var(--oa-text-muted);
  margin: 0;
}

// === Course header ===
.course-header-card {
  position: relative;
  overflow: hidden;
  border-radius: var(--oa-radius-xl);
  background: linear-gradient(135deg, var(--oa-bg-2), var(--oa-bg-1));
  padding: 32px;
  border: 1px solid var(--oa-border);

  @media (min-width: 768px) {
    padding: 40px;
  }
}

.header-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at top right, rgba(46, 127, 181, 0.15), transparent 50%);
}

.header-glow-bottom {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 192px;
  height: 192px;
  background: color-mix(in srgb, var(--oa-gold-full) 5%, transparent);
  border-radius: 50%;
  filter: blur(48px);
  transform: translateY(50%) translateX(-25%);
}

.header-content {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
}

.progress-circle-wrapper {
  position: relative;
  width: 96px;
  height: 96px;
  flex-shrink: 0;
}

.progress-svg {
  width: 96px;
  height: 96px;
  transform: rotate(-90deg);
}

.progress-ring {
  transition: stroke-dashoffset 0.7s ease;
}

.progress-circle-text {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-pct {
  font-size: var(--oa-text-lg);
  font-weight: 700;
  color: var(--oa-text-primary);
}

.header-info {
  flex: 1;
}

.breadcrumb-back {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 500;
  text-decoration: none;
  transition: color var(--oa-duration-fast) ease;

  &:hover {
    color: var(--oa-gold-full);
  }
}

.course-title {
  font-size: var(--oa-text-xl);
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 4px 0 0 0;
  line-height: 1.2;

  @media (min-width: 768px) {
    font-size: var(--oa-text-hero);
  }
}

.course-stats {
  color: var(--oa-text-secondary);
  margin: 4px 0 0 0;
  font-size: var(--oa-text-sm);
}

.continue-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: var(--oa-gold-full);
  color: var(--color-white-cap);
  font-weight: 600;
  border-radius: var(--oa-radius-lg);
  text-decoration: none;
  transition: background var(--oa-duration-fast) ease;
  box-shadow: 0 4px 16px rgba(46, 127, 181, 0.15);
  flex-shrink: 0;

  &:hover {
    background: var(--oa-gold-bright);
  }
}

// === Units ===
.units-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 32px;
}

.unit-card {
  border-radius: var(--oa-radius-xl);
  border: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
  background: var(--oa-bg-2);
  overflow: hidden;
  transition: box-shadow var(--oa-duration-normal) ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }
}

.unit-completed {
  background: color-mix(in srgb, var(--oa-success) 5%, var(--oa-bg-2));
  border-color: color-mix(in srgb, var(--oa-success) 15%, transparent);
}

.unit-locked {
  opacity: 0.6;
  background: color-mix(in srgb, var(--oa-bg-3) 30%, var(--oa-bg-2));
  border-color: color-mix(in srgb, var(--oa-border) 30%, transparent);
}

.unit-header {
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.unit-number {
  width: 40px;
  height: 40px;
  border-radius: var(--oa-radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--oa-text-sm);
  font-weight: 700;
  flex-shrink: 0;
  background: color-mix(in srgb, var(--oa-gold-full) 10%, transparent);
  color: var(--oa-gold-full);
}

.number-completed {
  background: color-mix(in srgb, var(--oa-success) 10%, transparent) !important;
  color: var(--oa-success) !important;
}

.number-locked {
  background: color-mix(in srgb, var(--oa-bg-3) 50%, transparent) !important;
  color: var(--oa-text-muted) !important;
}

.unit-info {
  flex: 1;
  min-width: 0;
}

.unit-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.unit-display-label {
  font-size: var(--oa-text-xs);
  font-family: var(--oa-font-mono);
  color: var(--oa-text-muted);
}

.unit-title {
  font-weight: 600;
  font-size: var(--oa-text-base);
  color: var(--oa-text-primary);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.title-locked {
  color: var(--oa-text-muted) !important;
}

.badge-completed {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  background: color-mix(in srgb, var(--oa-success) 12%, transparent);
  color: var(--oa-success);
}

.badge-progress {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  background: color-mix(in srgb, var(--oa-gold-full) 12%, transparent);
  color: var(--oa-gold-full);
}

.unit-progress-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
}

.unit-progress-track {
  flex: 1;
  height: 6px;
  background: color-mix(in srgb, var(--oa-bg-3) 50%, transparent);
  border-radius: 3px;
  overflow: hidden;
  max-width: 280px;
}

.unit-progress-fill {
  height: 100%;
  border-radius: 3px;
  background: var(--oa-gold-full);
  transition: width 0.5s ease;
}

.fill-complete {
  background: var(--oa-success) !important;
}

.unit-progress-text {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-muted);
  flex-shrink: 0;
}

// === Lessons ===
.unit-lessons {
  border-top: 1px solid color-mix(in srgb, var(--oa-border) 30%, transparent);
}

.lessons-locked {
  pointer-events: none;
  opacity: 0.4;
}

.lesson-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 24px;
  font-size: var(--oa-text-sm);
  text-decoration: none;
  color: inherit;
  transition: background var(--oa-duration-fast) ease;

  &:hover {
    background: color-mix(in srgb, var(--oa-gold-full) 3%, transparent);

    .lesson-arrow {
      color: var(--oa-gold-full);
      transform: translateX(2px);
    }

    .status-default {
      background: color-mix(in srgb, var(--oa-gold-full) 10%, transparent);
      color: var(--oa-gold-full);
    }
  }
}

.lesson-completed:hover {
  background: color-mix(in srgb, var(--oa-success) 3%, transparent);
}

.lesson-border {
  border-bottom: 1px solid color-mix(in srgb, var(--oa-border) 30%, transparent);
}

.lesson-status {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
  transition: all var(--oa-duration-fast) ease;
}

.status-completed {
  background: color-mix(in srgb, var(--oa-success) 10%, transparent);
  color: var(--oa-success);
}

.status-default {
  background: color-mix(in srgb, var(--oa-bg-3) 50%, transparent);
  color: var(--oa-text-muted);
}

.lesson-type-icon {
  color: var(--oa-text-disabled);
  font-size: var(--oa-text-xs);
  width: 16px;
  flex-shrink: 0;
  text-align: center;
}

.lesson-title {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--oa-text-secondary);
  transition: color var(--oa-duration-fast) ease;
}

.title-completed {
  color: var(--oa-text-muted) !important;
}

.lesson-arrow {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-disabled);
  transition: all var(--oa-duration-fast) ease;
}
</style>
